import { Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button as AriaButton, FileTrigger } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { classnames } from '@/utils/classnames.ts';

import Avatar from './Avatar.tsx';
import Button from './Button.tsx';

// What the API takes as images (api/core/images.py), checked here too, so nothing is saved with an image that fails.
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// What saving does to the image: upload a new file, remove it (null), or leave it (undefined).
export type ImageChange = File | null | undefined;

interface ImageEndpoints<T> {
    upload: (file: File) => Promise<T>;
    remove: () => Promise<T>;
}

// Saves the image on its own, once the form's thing is saved: uploads a new one, removes the stored one, or leaves it.
// Gives what was saved last, with its image.
export const saveImageChange = async <T,>(
    change: ImageChange,
    saved: T,
    hasImage: boolean,
    { upload, remove }: ImageEndpoints<T>
): Promise<T> => {
    if (change) return upload(change);
    if (change === null && hasImage) return remove();
    return saved;
};

export interface ImageFieldProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
    label: string;
    chooseLabel: string;
    hint?: string;
    // What the preview shows without an image: the initials, tinted by the hue.
    avatarName: string;
    hue?: number | null;
    // The stored image's.
    src: string | null;
    change: ImageChange;
    onChange: (change: ImageChange) => void;
    maxSize: number;
}

const ImageField = ({
    label,
    chooseLabel,
    hint,
    avatarName,
    hue,
    src,
    change,
    onChange,
    maxSize,
    className,
    ...props
}: ImageFieldProps) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const previewUrl = useMemo(() => (change instanceof File ? URL.createObjectURL(change) : null), [change]);
    useEffect(
        () => () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        },
        [previewUrl]
    );
    const shownSrc = change === undefined ? src : previewUrl;

    const handleSelect = (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        if (!IMAGE_TYPES.includes(file.type)) return setError(t('errors.api.image_type_not_allowed'));
        if (file.size > maxSize) return setError(t('errors.api.file_too_large'));
        setError(null);
        onChange(file);
    };

    return (
        <div {...props} className={classnames('flex flex-col gap-1.5', className)}>
            <span className="text-sm font-medium text-text">{label}</span>
            <div className="flex items-center gap-3">
                <Avatar name={avatarName || '?'} src={shownSrc ?? undefined} hue={hue} size="lg" />
                <FileTrigger acceptedFileTypes={IMAGE_TYPES} onSelect={handleSelect}>
                    <Button asChild variant="muted" size="sm">
                        <AriaButton>
                            <Upload />
                            {chooseLabel}
                        </AriaButton>
                    </Button>
                </FileTrigger>
                {shownSrc && (
                    <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
                        {t('imageField.remove')}
                    </Button>
                )}
            </div>
            {hint && <span className="text-sm text-text-muted">{hint}</span>}
            {error && <span className="text-sm text-negative">{error}</span>}
        </div>
    );
};

export default ImageField;
